import { useState, type ReactNode } from "react";
import { ChevronRight, Wrench } from "lucide-react";
import { cn } from "@vseh/ui";

/**
 * Когда AI вызвал инструмент — это явно показывается. По §14
 * «Tool call card».
 *
 * Свернуто по умолчанию. Имя инструмента — моноширинно.
 * Аргументы и результат (как JSON) — раскрываются по клику.
 */
export interface ToolCallCardProps {
  /** Полное имя: "<connector>.<capability>". */
  name: string;
  arguments: Record<string, unknown>;
  /** Результат — может быть произвольным JSON или коротким резюме. */
  result?: ReactNode;
  /** Краткое одностроконое резюме (напр. "→ 14 results"). Видно даже свёрнутым. */
  resultSummary?: string;
  latencyMs?: number;
  status?: "running" | "ok" | "error";
  className?: string;
}

export function ToolCallCard({
  name,
  arguments: args,
  result,
  resultSummary,
  latencyMs,
  status = "ok",
  className,
}: ToolCallCardProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn(
        "rounded-md border border-base-200 bg-base-50 text-sm",
        status === "error" && "border-danger-200 bg-danger-50",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-start gap-2 px-3 py-2 text-left",
          "hover:bg-base-100",
        )}
      >
        <Wrench
          aria-hidden="true"
          className={cn(
            "mt-0.5 size-4 shrink-0",
            status === "error" ? "text-danger-700" : "text-ai-700",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm text-base-900">{name}</code>
            {status === "running" && (
              <span className="text-2xs text-ai-700">в работе...</span>
            )}
            {latencyMs != null && status !== "running" && (
              <span className="text-2xs tabular-nums text-base-500">
                {(latencyMs / 1000).toFixed(2)}s
              </span>
            )}
          </div>
          {resultSummary && (
            <div
              className={cn(
                "mt-0.5 truncate text-xs",
                status === "error" ? "text-danger-700" : "text-base-600",
              )}
            >
              {resultSummary}
            </div>
          )}
        </div>
        <ChevronRight
          aria-hidden="true"
          className={cn(
            "size-4 shrink-0 text-base-400 transition-transform duration-fast",
            open && "rotate-90",
          )}
        />
      </button>

      {open && (
        <div className="space-y-2 border-t border-base-200 px-3 py-2">
          <div>
            <div className="mb-1 text-2xs font-medium text-base-700">arguments</div>
            <pre className="overflow-x-auto rounded-sm bg-base-900 px-2 py-1.5 text-2xs leading-relaxed text-base-100">
              <code>{JSON.stringify(args, null, 2)}</code>
            </pre>
          </div>
          {result !== undefined && (
            <div>
              <div className="mb-1 text-2xs font-medium text-base-700">result</div>
              {typeof result === "string" || typeof result === "number" ? (
                <pre className="overflow-x-auto rounded-sm bg-base-900 px-2 py-1.5 text-2xs leading-relaxed text-base-100">
                  <code>{String(result)}</code>
                </pre>
              ) : (
                <div className="text-xs text-base-700">{result}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
